<template>
  <!-- Unified compact buttons for both modes -->
  <div :class="['flex flex-col sm:flex-row sm:items-center', 'gap-3', groupGap.replace('gap-', 'sm:gap-')]">
    <!-- Filter Groups -->
    <div
      v-for="filterGroup in filterGroups"
      :key="filterGroup.name"
      :class="['flex flex-wrap items-center', itemGap]"
    >
      <button
        v-for="option in filterGroup.options"
        :key="option.value"
        :class="[
          buttonSizeClasses,
          'transition-colors',
          getFilterValue(filterGroup.name) === option.value
            ? filterGroup.activeClass || 'bg-indigo-600 text-white'
            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
        ]"
        @click="handleFilterClick(filterGroup.name, option.value)"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface FilterOption {
  value: string | number | undefined;
  label: string;
}

interface FilterGroup {
  name: string;
  options: FilterOption[];
  activeClass?: string;
  allowToggle?: boolean; // Allow toggle behavior (click same to deselect) for this specific filter group
}

interface Props {
  // Filter groups
  filterGroups: FilterGroup[];
  
  // Current filter values
  filterValues: Record<string, string | number | undefined>;
  
  // Layout options
  groupGap?: string; // Gap between filter groups (default: 'gap-6')
  itemGap?: string; // Gap between items within each filter group (default: 'gap-2')
  
  // Button size
  size?: 'md' | 'lg'; // Button size (default: 'md')
}

const props = withDefaults(defineProps<Props>(), {
  filterGroups: () => [],
  filterValues: () => ({}),
  groupGap: 'gap-6',
  itemGap: 'gap-2',
  size: 'md'
});

const emit = defineEmits<{
  'filter-click': [groupName: string, value: string | number | undefined];
}>();

// Computed property for button size classes
const buttonSizeClasses = computed(() => {
  return props.size === 'lg' 
    ? 'px-4 py-2 text-sm font-medium rounded-lg'
    : 'px-3 py-1 text-xs font-medium rounded-md';
});

// Get current filter value for a group
const getFilterValue = (groupName: string): string | number | undefined => {
  return props.filterValues[groupName];
};

// Handle filter click with toggle logic based on group's allowToggle
const handleFilterClick = (groupName: string, value: string | number | undefined) => {
  const filterGroup = props.filterGroups.find(g => g.name === groupName);
  const allowToggle = filterGroup?.allowToggle ?? true; // Default to true if not specified
  
  let newValue: string | number | undefined;
  if (getFilterValue(groupName) === value && allowToggle) {
    newValue = undefined; // 'all' is now undefined
  } else {
    newValue = value;
  }
  
  emit('filter-click', groupName, newValue);
};
</script>

<style scoped>
/* Smooth scrolling for tabs on mobile */
.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */
}

.overflow-x-auto::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, Opera */
}
</style>
